import {
	faBuildingColumns,
	faCreditCard,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { Tooltip } from '../overlays/Tooltip';
import {
	BrandAmex,
	BrandDiners,
	BrandDiscover,
	BrandJcb,
	BrandMastercard,
	BrandVisa,
} from './brand-marks';

/**
 * PD-15878 — the card network's own mark, in its own colours, wherever a
 * payment method is named or offered.
 *
 * Customers recognise a card by its logo long before they read four digits,
 * and a form that shows the marks it accepts answers "can I pay with this?"
 * without anyone having to ask. Every surface that lists or accepts a method
 * renders through this component, so the artwork, the sizing and the fallback
 * stay identical between the CRM and the storefront.
 *
 * The marks are inline SVG rather than an icon font because a font glyph is
 * single-colour by construction — Mastercard's whole recognisability is the
 * red and yellow circles, and a grey silhouette of them is not the logo. The
 * artwork is the networks' own, vendored in `brand-marks.tsx` (see the note
 * there for source and licence) rather than approximated by hand.
 *
 * The brand vocabulary is `PaymentMethodSubtypeEnum` — the same strings the
 * card detector produces and the gateway stores on a saved method — matched
 * case-insensitively so a gateway reporting "VISA" or "american express" still
 * resolves. Anything unrecognised falls back to a neutral card (or a bank mark
 * for an account-based method) rather than rendering nothing, so a row never
 * loses its leading icon.
 */

/** Every vendored mark shares the source set's 780×500 card viewport. */
const VIEW_BOX = '0 0 780 500';

const BRAND_MARKS: Record<string, () => JSX.Element> = {
	visa: BrandVisa,
	mastercard: BrandMastercard,
	amex: BrandAmex,
	'american express': BrandAmex,
	discover: BrandDiscover,
	'diners club': BrandDiners,
	dinersclub: BrandDiners,
	jcb: BrandJcb,
};

/** The bank mark reads better than a card for an account-based method. */
const BANK_SUBTYPES = new Set(['checking', 'savings', 'ach', 'account funds']);

const normalize = (brand: string | null | undefined): string =>
	(brand ?? '').trim().toLowerCase();

/** Whether a real network mark exists for this brand (vs. the fallback icon). */
export function hasCardBrandIcon(brand: string | null | undefined): boolean {
	return BRAND_MARKS[normalize(brand)] != null;
}

export interface CardBrandIconProps {
	/** The method's subtype — 'Visa', 'MasterCard', 'Checking', … */
	brand: string | null | undefined;
	className?: string;
	/**
	 * Accessible name. Defaults to the brand itself; pass a fuller label where
	 * the icon is the row's only identification.
	 */
	title?: string;
}

export function CardBrandIcon({
	brand,
	className,
	title,
}: CardBrandIconProps): JSX.Element {
	const key = normalize(brand);
	const label =
		title ?? (brand != null && brand !== '' ? brand : 'Payment method');
	const Mark = BRAND_MARKS[key];

	// A logo without a name is a guess for anyone who does not recognise it, so
	// every mark says what it is on hover. The shared Tooltip rather than the
	// native `title` attribute, per the repo's component guidance: `title` is
	// unstyled, slow to appear and invisible on touch.
	const mark =
		Mark == null ? (
			<FontAwesomeIcon
				icon={BANK_SUBTYPES.has(key) ? faBuildingColumns : faCreditCard}
				className={className}
				aria-label={label}
			/>
		) : (
			// Sized in `em` so a caller's font-size class controls it exactly as
			// it controls the FontAwesome fallback beside it — the two must not
			// disagree about how big "one icon" is.
			<svg
				viewBox={VIEW_BOX}
				role="img"
				aria-label={label}
				// Each mark carries its own card background, so it stands away
				// from a light surface on its own. On a dark one the darker
				// marks (Mastercard, Discover, JCB) can lose their edge against
				// the page, so a hairline ring defines it — cheaper and less to
				// maintain than a second set of artwork for dark mode.
				className={`rounded-[0.1em] dark:ring-1 dark:ring-white/20 ${className ?? ''}`}
				style={{ width: '1.56em', height: '1em' }}
			>
				<Mark />
			</svg>
		);

	return (
		// The Tooltip wraps its child in a plain block div. Left alone that div
		// inherits the row's line-height and the mark rides the line box's
		// baseline, sitting a few pixels above the label beside it. Making the
		// wrapper a centred flex box — and collapsing the leading inside it —
		// lets the row's own items-center line the two up exactly.
		<Tooltip
			content={label}
			position="top"
			className="inline-flex items-center leading-none"
		>
			<span className="inline-flex items-center leading-none">{mark}</span>
		</Tooltip>
	);
}
